import { WorkerInfo, Answer, Experience, Education, Skill } from '@/types/workerInfo.types';
import cssContent from '../assets/cv-preview.css?raw';

async function imageUrlToBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function calculateAge(birthDate: string): number {
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

function getCurrentDate(): string {
  const today = new Date();
  return `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;
}

function escapeHtml(str: string): string {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatDocumentNumber(num: string): string {
  if (!num) return '';
  return num.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function extractPersonalDataById(answers: Answer[]) {
  const answerMap = new Map<string, Answer>();
  answers.forEach(a => {
    if (a.questionId) answerMap.set(a.questionId, a);
  });

  const nameId = "54dbc76a-c3a4-4660-8989-01af0ae5263f";
  const lastNameId = "7c02b03b-f577-4b56-b28b-8e843fba5f8e";
  const cityId = "94a47fa1-6b29-4818-928b-05979f578d3c";
  const phoneId = "75afd0e9-e8da-4982-8d9b-1a89d93e5f6d";
  const emailId = "36ddb795-0336-4e21-894c-e1416818da43";
  const birthDateId = "42c8a67e-ce5a-4772-8f58-87c978990230";
  const documentTypeId = "54ec55aa-19a3-42fd-b357-13625bb17639";
  const documentNumberId = "e18adf9e-b9f2-4e0c-a473-d3d67dcfa092";
  const maritalStatusId = "46dd9620-5caf-4a9d-90b3-e3814ccce177";
  const hasChildrenId = "02d768d3-a84e-4847-a569-7c0e02f4492f";
  const childrenCountId = "8e7a2a6a-62fb-46c0-8583-a735cb9ab597";
  const livesWithChildrenId = "cdc2cb76-68cf-46c8-ba94-00cfa2556b6f";
  const availabilityId = "aaf19aa4-4690-4812-bde3-6638f14df53b";
  const geographicAvailabilityId = "47ab1dee-a285-4491-89bb-dfff75812213";
  const licenseTypesId = "30095bc8-d24b-460e-b1f2-2929922f19a8";
  const descriptionId = "2b81ba2f-32ee-4315-944f-c8d02b0215a8";

  const getValue = (id: string) => answerMap.get(id);
  const nameAnswer = getValue(nameId);
  const lastNameAnswer = getValue(lastNameId);
  const fullName = `${nameAnswer?.textValue || ''} ${lastNameAnswer?.textValue || ''}`.toUpperCase().trim();

  const birthDate = getValue(birthDateId)?.dateValue || '';
  const age = birthDate ? calculateAge(birthDate) : 0;

  const hasChildren = getValue(hasChildrenId)?.booleanValue || false;
  const childrenCount = getValue(childrenCountId)?.numberValue || '0';
  const livesWithChildren = getValue(livesWithChildrenId)?.booleanValue || false;

  const availabilityAnswer = getValue(availabilityId);
  const availability = availabilityAnswer?.arrayValue?.[0] || availabilityAnswer?.textValue || 'No especificada';

  const geographicAnswer = getValue(geographicAvailabilityId);
  const geographicAvailabilityVal = geographicAnswer?.arrayValue?.join(', ') || geographicAnswer?.textValue || 'No especificada';

  const licenseAnswer = getValue(licenseTypesId);
  const licenseTypesArr = licenseAnswer?.arrayValue || (licenseAnswer?.textValue ? [licenseAnswer.textValue] : []);

  return {
    fullName: fullName || 'NO REGISTRADO',
    city: getValue(cityId)?.textValue || '',
    phone: getValue(phoneId)?.textValue || '',
    email: getValue(emailId)?.textValue || '',
    birthDate,
    age,
    documentType: getValue(documentTypeId)?.textValue || '',
    documentNumber: getValue(documentNumberId)?.textValue || '',
    maritalStatus: getValue(maritalStatusId)?.textValue || '',
    hasChildren,
    childrenInfo: hasChildren ? `${childrenCount} hijo(s) a cargo ${!livesWithChildren ? '(No vive con él/ella)' : ''}` : 'Sin hijos',
    availability,
    geographicAvailability: geographicAvailabilityVal,
    licenseTypes: licenseTypesArr.join(', '),
    licenseTypesArr,
    description: getValue(descriptionId)?.textValue || ''
  };
}

function extractExperiences(answers: Answer[]): Experience[] {
  const experienceAnswer = answers.find(a => a.question?.type === 'experience');
  return experienceAnswer?.experienceValue || [];
}

function extractEducations(answers: Answer[]): Education[] {
  const educationAnswer = answers.find(a => a.question?.type === 'education');
  return educationAnswer?.educationValue || [];
}

function extractSkills(answers: Answer[]): Skill[] {
  const skillAnswer = answers.find(a => a.question?.type === 'skill');
  return skillAnswer?.skillValue || [];
}

function extractDocuments(answers: Answer[], experiences: Experience[], educations: Education[]): any[] {
  const documents: any[] = [];

  // Document-type questions with uploaded files — dinámico, sin mapeo fijo
  for (const answer of answers) {
    if (answer.question?.type === 'document' && answer.documentValue && answer.documentValue.length > 0) {
      for (const doc of answer.documentValue) {
        documents.push({
          name: answer.question.question || 'Documento',
          status: 'VALIDADO',
          emissionDate: '',
          url: doc.url || '#'
        });
      }
    }
  }

  // Certificados de experiencias
  for (const exp of experiences) {
    if (exp.hasCertificate && exp.certificateUrl) {
      documents.push({
        name: `Certificado de experiencia - ${exp.company}`,
        status: 'VALIDADO',
        emissionDate: '',
        url: exp.certificateUrl
      });
    }
  }

  // Certificados de educación
  for (const edu of educations) {
    if (edu.hasCertificate && edu.certificateUrl) {
      documents.push({
        name: `Certificado de educación - (${edu.institution})`,
        status: 'VALIDADO',
        emissionDate: '',
        url: edu.certificateUrl
      });
    }
  }

  return documents;
}

function extractServicesWorked(answers: Answer[]): string[] {
  const answer = answers.find(a => a.question?.question?.toLowerCase().includes('ha trabajado en tipos de servicios'));
  return answer?.arrayValue || [];
}

function extractVehiclesOperated(answers: Answer[]): string[] {
  const answer = answers.find(a =>
    a.question?.question?.toLowerCase().includes('tipo de trabajo') ||
    a.question?.question?.toLowerCase().includes('cargo que desempeña')
  );
  return answer?.arrayValue || [];
}

function extractWorkModalities(answers: Answer[]): string[] {
  const answer = answers.find(a => a.question?.question?.toLowerCase().includes('le gustaria trabajar por modalidad'));
  return answer?.arrayValue || [];
}

function extractAvailability(answers: Answer[]) {
  const availabilityAnswer = answers.find(a => a.question?.question?.toLowerCase().includes('actualmente esta con disponibilidad'));
  const geographicAnswer = answers.find(a => a.question?.question?.toLowerCase().includes('disponibilidad para trabajar en jurisdicción'));

  return {
    availability: availabilityAnswer?.arrayValue?.[0] || 'No especificada',
    geographicAvailability: geographicAnswer?.arrayValue?.join(', ') || 'No especificada'
  };
}

function extractLicenseTypes(answers: Answer[]): string[] {
  const answer = answers.find(a => a.question?.question?.toLowerCase().includes('tipo de licencia de conducción'));
  return answer?.arrayValue || [];
}

export async function generatePreviewHtml(profile: WorkerInfo, referralCode?: string): Promise<string> {
  const answers = profile.answers || [];
  const personalData = extractPersonalDataById(answers);
  const experiences = extractExperiences(answers);
  const educations = extractEducations(answers);
  const documents = extractDocuments(answers, experiences, educations);
  const skills = extractSkills(answers);
  const servicesWorked = extractServicesWorked(answers);
  const vehiclesOperated = extractVehiclesOperated(answers);
  const workModalites = extractWorkModalities(answers);
  const availability = extractAvailability(answers);
  const licenseTypes = extractLicenseTypes(answers);
  const profileId = referralCode || `WD-${profile.id.slice(0, 8)}`;

  const experiencesHtml = experiences.slice(0, 3).map((exp: Experience) => {
    const startYear = new Date(exp.startDate).getFullYear();
    const endYear = exp.current ? 'Actualidad' : exp.endDate ? new Date(exp.endDate).getFullYear() : '';
    return `
      <div class="timeline-item">
        <div class="timeline-years">${startYear} - ${endYear}</div>
        <div class="timeline-content">
          <div class="timeline-company">${escapeHtml(exp.company)}</div>
          <div class="timeline-detail">Cargo: ${escapeHtml(exp.position)}</div>
          ${exp.description ? `<div class="timeline-detail">${escapeHtml(exp.description)}</div>` : ''}
        </div>
      </div>
    `;
  }).join('');

  const educationListHtml = educations.map((edu: Education) => {
    const degree = edu.degree === 'pregrado' ? 'Pregrado' : edu.degree;
    return `<li>${degree} - ${escapeHtml(edu.field)} (${escapeHtml(edu.institution)})</li>`;
  }).join('');

  const skillsListHtml = skills.map((skill: Skill) => {
    return `<li>${escapeHtml(skill.name)} - ${skill.level} (${skill.yearsOfExperience} años)</li>`;
  }).join('');

  const servicesHtml = servicesWorked.map((service: string) => `
    <div class="service-item">
      <span class="service-icon"><i class="fas fa-shield-alt"></i></span>
      <span class="service-text">${escapeHtml(service)}</span>
    </div>
  `).join('');

  const vehiclesHtml = vehiclesOperated.map((vehicle: string) => `
    <div class="vehicle-item">
      <span class="vehicle-icon"><i class="fas fa-bus"></i></span>
      <span class="vehicle-name">${escapeHtml(vehicle.replace('Conductor ', ''))}</span>
    </div>
  `).join('');

  const documentsHtml = documents.map((doc: any) => `
    <div class="document-row">
      <div class="table-cell">
        <span class="doc-icon"><i class="fas fa-id-card"></i></span>
        <a href="${doc.url}" target="_blank" rel="noreferrer noopener" class="doc-name-link">${escapeHtml(doc.name)}</a>
      </div>
      <div class="table-cell">
        <span class="status-badge validated"><i class="fas fa-check"></i> ${doc.status}</span>
      </div>
      <div class="table-cell date-cell">${doc.emissionDate || '25/05/2026'}</div>
      <div class="table-cell action-cell">
        <a href="${doc.url}" target="_blank" rel="noreferrer noopener" style="display:inline-flex; align-items:center; justify-content:center; padding:8px 12px; border-radius:8px; background:#0b3c91; color:#ffffff; text-decoration:none; font-size:11px; font-weight:700;">Abrir</a>
      </div>
    </div>
  `).join('');

  const hasExperience = experiences.length > 0;
  const hasLicense = licenseTypes.length > 0;

  let profileImageBase64: string | null = null;
  if (profile.profileImage) {
    profileImageBase64 = await imageUrlToBase64(profile.profileImage);
  }

  return `<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>WingDrive - Perfil Profesional ${escapeHtml(personalData.fullName)}</title>
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css">
    <style>${cssContent}</style>
</head>
<body>
    <div class="watermark">
        WINGDRIVE
        <span>902022015 - 7</span>
    </div>
    <div class="page page-1">
        <div class="page-content">
            <div class="header">
                <div class="header-left">
                    <div class="logo">W WINGDRIVE™</div>
                    <div class="subtitle">PLATAFORMA TECNOLÓGICA<br>PARA EL TRANSPORTE COLOMBIANO</div>
                </div>
                <div class="header-right">
                    <div class="id-perfil">
                        <span class="id-label">ID PERFIL</span>
                        <span class="id-value">${escapeHtml(profileId)}</span>
                    </div>
                    <div class="qr-box">
                        <div class="qr-text">Validar perfil<br>en línea</div>
                    </div>
                    <div class="verified-badge">
                        <span class="check-icon"><i class="fas fa-check-circle"></i></span>
                        <span class="badge-text">PERFIL<br>VERIFICADO</span>
                    </div>
                </div>
            </div>

            <div class="profile-content">
                <div class="profile-left">
                    <div class="profile-image">
                        ${profileImageBase64 ?
                          `<img src="${profileImageBase64}" alt="Profile" style="width: 100%; height: 100%; object-fit: cover; border-radius: 12px;">` :
                          `<div class="image-placeholder"></div>`}
                    </div>

                    <div class="info-section personal-info">
                        <div class="section-title-dark">INFORMACIÓN PERSONAL</div>
                        <div class="info-list">
                            <div class="info-item">
                                <span class="info-icon"><i class="fas fa-map-marker-alt"></i></span>
                                <span class="info-text">${escapeHtml(personalData.city)}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-icon"><i class="fas fa-phone-alt"></i></span>
                                <span class="info-text">${escapeHtml(personalData.phone)}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-icon"><i class="fas fa-envelope"></i></span>
                                <span class="info-text">${escapeHtml(personalData.email)}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-icon"><i class="fas fa-birthday-cake"></i></span>
                                <span class="info-text">${escapeHtml(personalData.birthDate)} · ${personalData.age} años</span>
                            </div>
                            <div class="info-item">
                                <span class="info-icon"><i class="fas fa-id-card"></i></span>
                                <span class="info-text">${escapeHtml(personalData.documentType)}: ${formatDocumentNumber(personalData.documentNumber)}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-icon"><i class="fas fa-heart"></i></span>
                                <span class="info-text">${escapeHtml(personalData.maritalStatus)}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-icon"><i class="fas fa-child"></i></span>
                                <span class="info-text">${escapeHtml(personalData.childrenInfo)}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-icon"><i class="fas fa-bolt"></i></span>
                                <span class="info-text">Disponibilidad: ${escapeHtml(availability.availability)}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-icon"><i class="fas fa-globe-americas"></i></span>
                                <span class="info-text">Disponibilidad geográfica:<br><span style="margin-left: 20px;">${escapeHtml(availability.geographicAvailability)}</span></span>
                            </div>
                        </div>
                    </div>

                    <div class="info-section why-contract">
                        <div class="section-title-dark">¿POR QUÉ CONTRATARME?</div>
                        <div class="checkbox-list">
                            ${hasExperience ? '<div class="checkbox-item"><span class="checkbox"><i class="fas fa-check"></i></span><span class="text">Experiencia comprobada en transporte</span></div>' : ''}
                            ${hasLicense ? '<div class="checkbox-item"><span class="checkbox"><i class="fas fa-check"></i></span><span class="text">Licencias vigentes: ' + licenseTypes.join(', ') + '</span></div>' : ''}
                            <div class="checkbox-item"><span class="checkbox"><i class="fas fa-check"></i></span><span class="text">Responsable, puntual y comprometido con la seguridad</span></div>
                            <div class="checkbox-item"><span class="checkbox"><i class="fas fa-check"></i></span><span class="text">Excelente trato al cliente</span></div>
                            ${workModalites.length > 0 ? '<div class="checkbox-item"><span class="checkbox"><i class="fas fa-check"></i></span><span class="text">Disponible para trabajar por: ' + workModalites.join(', ') + '</span></div>' : ''}
                        </div>
                    </div>
                </div>

                <div class="profile-right">
                    <div class="name-section">
                        <h1 class="profile-name">${escapeHtml(personalData.fullName)}</h1>
                        <p class="profile-title">CONDUCTOR PROFESIONAL</p>
                        <div class="licenses-badges">
                            ${licenseTypes.map((l: string) => `<span class="license-badge license-${l.toLowerCase()}">${l}</span>`).join('')}
                            <span class="license-badge license-vigente">VIGENTES</span>
                        </div>
                    </div>

                    <div class="quote-section">
                        <p class="quote-text">${escapeHtml(personalData.description || 'Conductor profesional con experiencia en transporte de pasajeros. Comprometido con la seguridad, la puntualidad y el servicio de calidad.')}</p>
                    </div>

                    <div class="availability-badge">
                        <div class="availability-label"><i class="fas fa-clock"></i> DISPONIBILIDAD INMEDIATA</div>
                    </div>

                    <div class="experience-section">
                        <h3 class="section-title"><i class="fas fa-briefcase"></i> EXPERIENCIA LABORAL DESTACADA</h3>
                        <div class="timeline">
                            ${experiencesHtml || '<div class="timeline-item"><div class="timeline-content">Sin experiencia registrada</div></div>'}
                        </div>
                    </div>

                    <div class="services-section">
                        <h3 class="section-title"><i class="fas fa-concierge-bell"></i> TIPOS DE SERVICIOS EN LOS QUE HE TRABAJADO</h3>
                        <div class="services-grid">
                            ${servicesHtml || '<div class="service-item"><span class="service-text">No hay servicios registrados</span></div>'}
                        </div>
                    </div>

                    <div class="vehicles-section">
                        <h3 class="section-title"><i class="fas fa-truck"></i> VEHÍCULOS QUE OPERO</h3>
                        <div class="vehicles-grid">
                            ${vehiclesHtml || '<div class="vehicle-item"><span class="vehicle-name">No hay vehículos registrados</span></div>'}
                        </div>
                    </div>

                    <div class="formation-section">
                        <h3 class="section-title"><i class="fas fa-graduation-cap"></i> FORMACIÓN Y CERTIFICACIONES</h3>
                        <ul class="formation-list">
                            ${educationListHtml || '<li>Bachiller Académico</li><li>Manejo Defensivo</li><li>Servicio al Cliente</li>'}
                        </ul>
                    </div>

                    <div class="competencies-section">
                        <h3 class="section-title"><i class="fas fa-chart-line"></i> COMPETENCIAS Y HABILIDADES</h3>
                        <ul class="competencies-list">
                            ${skillsListHtml || '<li>Conducción segura y responsable</li><li>Atención y servicio al cliente</li><li>Trabajo en equipo</li><li>Puntualidad y cumplimiento</li>'}
                        </ul>
                    </div>
                </div>
            </div>

            <div class="footer-badge">
                <div class="badge-content">
                    <span class="shield-icon"><i class="fas fa-shield-alt"></i></span>
                    <div class="badge-text">
                        <strong>PERFIL VALIDADO POR WINGDRIVE™</strong>
                        <span>Este perfil ha sido verificado en nuestras fuentes oficiales.</span>
                    </div>
                </div>
                <div class="verification-items">
                    <span class="verification-item"><i class="fas fa-check-circle"></i> Verificado</span>
                    <span class="verification-item"><i class="fas fa-handshake"></i> Confiable</span>
                    <span class="verification-item"><i class="fas fa-briefcase"></i> Profesional</span>
                    <span class="verification-item"><i class="fas fa-calendar-check"></i> Responsable</span>
                </div>
            </div>

            <div class="page-footer">
                <div class="footer-left">
                    <span class="footer-logo">W WINGDRIVE™</span>
                    <span class="footer-text">Plataforma tecnológica para el transporte colombiano</span>
                </div>
                <div class="footer-right">
                    <span><i class="fas fa-globe"></i> www.wingdrive.com.co</span>
                    <span><i class="fas fa-phone-alt"></i> +57 3148690295</span>
                    <span><i class="fas fa-file-invoice"></i> NIT: 902022015 - 7</span>
                </div>
            </div>
        </div>
    </div>

    <div class="page page-2">
        <div class="page-content">
            <div class="header">
                <div class="header-left">
                    <div class="logo">W WINGDRIVE™</div>
                </div>
                <div class="header-right">
                    <div class="id-perfil">
                        <span class="id-label">ID PERFIL</span>
                        <span class="id-value">${escapeHtml(profileId)}</span>
                    </div>
                </div>
            </div>

            <div class="validation-content">
                <h1 class="validation-title"><i class="fas fa-file-alt"></i> CENTRO DE VALIDACIÓN DOCUMENTAL</h1>
                <p class="validation-subtitle">Documentos verificados en fuentes oficiales</p>

                <div class="status-box">
                    <div class="status-left">
                        <div class="status-icon"><i class="fas fa-check-circle"></i></div>
                        <div class="status-title">ESTADO GENERAL DE DOCUMENTACIÓN</div>
                        <div class="status-detail">${documents.length} de ${Math.max(documents.length, 12)} documentos validados</div>
                    </div>
                    <div class="status-right">
                        <div class="percentage">${Math.round((documents.length / Math.max(documents.length, 12)) * 100)}%</div>
                        <div class="percentage-text">COMPLETA</div>
                    </div>
                </div>

                <div class="documents-section">
                    <div class="table-header">
                        <div class="table-cell">DOCUMENTO</div>
                        <div class="table-cell">ESTADO</div>
                        <div class="table-cell">FECHA DE EMISIÓN</div>
                        <div class="table-cell">ACCIÓN</div>
                    </div>
                    <div class="documents-list">
                        ${documentsHtml}
                    </div>
                </div>

                <div class="realtime-section">
                    <h3 class="realtime-title"><i class="fas fa-sync-alt"></i> VERIFICACIÓN EN TIEMPO REAL</h3>
                    <div class="realtime-info">
                        <div class="realtime-item">
                            <span class="realtime-label">Última validación</span>
                            <span class="realtime-value">${getCurrentDate()}</span>
                        </div>
                        <div class="realtime-item">
                            <span class="realtime-label"></span>
                            <span class="realtime-value">${new Date().toLocaleTimeString('es-CO')}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="motivational-box">
                <p class="motivational-text"><i class="fas fa-star"></i> "El héroe eres tú,<br>tu poder está al volante." <i class="fas fa-star"></i></p>
            </div>
            <div style="text-align:center; margin-top:15px;">
                <img src="/src/assets/logo.png" alt="Logo" style="max-width:120px; height:auto;" />
            </div>

            <div class="page-footer">
                <div class="footer-left">
                    <span class="footer-logo">W WINGDRIVE™</span>
                    <span class="footer-text">Plataforma tecnológica para el transporte colombiano</span>
                </div>
                <div class="footer-right">
                    <a href="http://www.wingdrive.com.co" target="_blank"><span><i class="fas fa-globe"></i> www.wingdrive.com.co</span></a>
                    <a href="tel:+573148690295"><span><i class="fas fa-phone-alt"></i> +57 3148690295</span></a>
                    <span><i class="fas fa-file-invoice"></i> NIT: 902022015 - 7</span>
                </div>
            </div>
        </div>
    </div>

    <div class="page page-3">
        <div class="page-content">
            <div class="header">
                <div class="header-left">
                    <div class="logo">W WINGDRIVE™</div>
                </div>
                <div class="header-right">
                    <div class="id-perfil">
                        <span class="id-label">ID PERFIL</span>
                        <span class="id-value">${escapeHtml(profileId)}</span>
                    </div>
                </div>
            </div>

            <div class="benefits-content">
                <h1 class="benefits-main-title"><i class="fas fa-gem"></i> MIS BENEFICIOS CON WINGDRIVE™</h1>

                <div class="benefits-grid">
                    <div class="benefit-card">
                        <div class="benefit-icon"><i class="fas fa-building"></i></div>
                        <p class="benefit-text">Mi perfil será para empresas de transporte verificadas</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon"><i class="fas fa-chart-line"></i></div>
                        <p class="benefit-text">Más oportunidades laborales</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon"><i class="fas fa-shield-alt"></i></div>
                        <p class="benefit-text">Proceso seguro y transparente</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon"><i class="fas fa-trophy"></i></div>
                        <p class="benefit-text">Crecimiento profesional</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon"><i class="fas fa-handshake"></i></div>
                        <p class="benefit-text">Respaldo y acompañamiento</p>
                    </div>
                </div>

                <div class="update-documents-box">
                    <div class="update-left">
                        <p class="update-title"><i class="fas fa-check-circle"></i> ¡Tu perfil está listo para nuevas oportunidades!</p>
                        <p class="update-text">Mantén tu documentación actualizada y sigue creciendo con WingDrive™.</p>
                    </div>
                    <div class="update-right">
                        <button class="update-button"><i class="fas fa-upload"></i> Actualizar documentos</button>
                        <div class="update-icon"><i class="fas fa-mobile-alt"></i></div>
                    </div>
                </div>

                <div class="final-motivation">
                    <p class="motivation-quote"><i class="fas fa-quote-left"></i> "El héroe eres tú,<br>tu poder está al volante." <i class="fas fa-quote-right"></i></p>
                </div>
            </div>

            <div class="page-footer footer-extended">
                <div class="footer-content">
                    <div class="footer-main">
                        <span class="footer-logo">W WINGDRIVE™</span>
                        <span class="footer-text">Plataforma tecnológica para el transporte colombiano</span>
                    </div>
                    <div class="footer-contact">
                        <a href="http://www.wingdrive.com.co" target="_blank"><span><i class="fas fa-globe"></i> www.wingdrive.com.co</span></a>
                        <a href="tel:+573148690295"><span><i class="fas fa-phone-alt"></i> +57 3148690295</span></a>
                        <span><i class="fas fa-file-invoice"></i> NIT: 902022015 - 7</span>
                    </div>
                    <div class="footer-social">
                        <a href="https://www.facebook.com/share/19ak2d3aqB/?mibextid=wwXIfr" target="_blank" class="social-icon"><i class="fab fa-facebook-f"></i></a>
                        <a href="https://www.instagram.com/wingdrive_" target="_blank" class="social-icon"><i class="fab fa-instagram"></i></a>
                        <a href="https://www.tiktok.com/@wingdrive.drive?_r=1&_t=ZS-955DBZUlI6H" target="_blank" class="social-icon"><i class="fab fa-tiktok"></i></a>
                        <a href="https://www.x.com/wingdriveco?s=11" target="_blank" class="social-icon"><i class="fab fa-x"></i></a>
                        <a href="https://t.me/wingdrive" target="_blank" class="social-icon"><i class="fab fa-telegram"></i></a>
                        <a href="https://wa.me/573148690295" target="_blank" class="social-icon"><i class="fab fa-whatsapp"></i></a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</body>
</html>`;
}
